// react
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';

// material ui
import { alpha, Avatar, Box, Chip, Tooltip, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// api
import { usePutEnergyInitiativeMutation } from '@/api/recalcV2/endpoints/energyInitiativesApiSlice';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { showSnackbar } from '@/redux/snackbarSlice';
import { formatNumber } from '@/utils/formatters';

// relative
import StateChip from './helpers';
import InitiativeActionCell from './InitiativeActionsCell';
import InitiativeTimestampSummaryIndicator from './InitiativeTimestampSummaryIndicator';

import { DateTime } from 'luxon';

const getTrendColor = (percentage) => {
  if (percentage >= 0) {
    return 'success';
  } else {
    return 'error';
  }
};

const toApiTS = (input) => {
  if (!input) return null;
  let dt;
  if (input instanceof Date) dt = DateTime.fromJSDate(input);
  else if (typeof input === 'number') dt = DateTime.fromMillis(input);
  else dt = DateTime.fromISO(String(input), { setZone: true });
  if (!dt.isValid) return null;
  return dt.toUTC().toISO();
};

const deriveState = (ini) => {
  const { baselineStart, baselineEnd, comparisonStart, comparisonEnd } = ini;
  if (baselineStart && baselineEnd && comparisonStart && comparisonEnd)
    return 'Done';
  if (baselineStart && baselineEnd && comparisonStart) return 'Active';
  if (baselineStart || baselineEnd) return 'Initialized';
  if (baselineStart === null) return 'Created';
  return 'Created';
};

const uiDateChangesToApiPatch = (changes = {}) => {
  const patch = {};
  const has = (k) => Object.prototype.hasOwnProperty.call(changes, k);
  if (has('baselineStart'))
    patch.baselineStart = changes.baselineStart
      ? toApiTS(changes.baselineStart)
      : null;
  if (has('baselineEnd'))
    patch.baselineEnd = changes.baselineEnd
      ? toApiTS(changes.baselineEnd)
      : null;
  if (has('comparisonStart'))
    patch.comparisonStart = changes.comparisonStart
      ? toApiTS(changes.comparisonStart)
      : null;
  if (has('comparisonEnd'))
    patch.comparisonEnd = changes.comparisonEnd
      ? toApiTS(changes.comparisonEnd)
      : null;
  return patch;
};

const buildPutBody = (prevRaw = {}, uiChanges = {}) => {
  const datePatch = uiDateChangesToApiPatch(uiChanges);
  const next = { ...prevRaw, ...datePatch };
  if (Object.prototype.hasOwnProperty.call(datePatch, 'comparisonEnd')) {
    next.completed = datePatch.comparisonEnd ?? prevRaw.completed ?? null;
  }
  const norm = (v) => (v ? toApiTS(v) : null);
  const newState = deriveState(next);
  return {
    ...prevRaw,
    name: next.name ?? '',
    description: next.description ?? '',
    state: newState,
    completed: norm(next.completed),
    baselineStart: norm(next.baselineStart),
    baselineEnd: norm(next.baselineEnd),
    comparisonStart: norm(next.comparisonStart),
    comparisonEnd: norm(next.comparisonEnd),
    preferredModel:
      typeof next.preferredModel === 'number' ? next.preferredModel : 0,
    energyTreeNodes: Array.isArray(next.energyTreeNodes)
      ? next.energyTreeNodes
      : [],
  };
};

const formatDateRange = (startISO, endISO) => {
  if (!startISO && !endISO) return '—';

  const formatter = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  if (startISO && !endISO) return `${formatter.format(new Date(startISO))} –`;
  if (!startISO && endISO) return `– ${formatter.format(new Date(endISO))}`;

  const start = new Date(startISO);
  const end = new Date(endISO);

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  const sameMonth =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth();

  const sameYear = start.getFullYear() === end.getFullYear();

  const dayMonthFormatter = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
  });

  const fullFormatter = formatter;

  if (sameDay) return fullFormatter.format(start);
  if (sameMonth) return `${start.getDate()}.–${fullFormatter.format(end)}`;
  if (sameYear)
    return `${dayMonthFormatter.format(start)} – ${fullFormatter.format(end)}`;
  return `${fullFormatter.format(start)} – ${fullFormatter.format(end)}`;
};

const InitiativeDataGrid = ({
  initiatives,
  energyGroup,
  handleViewInitiative,
  handleEditInitiative,
  handleDeleteInitiative,
  isLoading,
  initiativeStateFilter,
}) => {
  const { t } = useTranslation('energyxplorer-initiatives-tab');
  const localeText = useDatagridLocaleText();

  const dispatch = useDispatch();
  const [putEnergyInitiative] = usePutEnergyInitiativeMutation();
  const [overrides, setOverrides] = React.useState({});

  const columnVisibilityModel = useMemo(
    () => ({
      co2eSavings: initiativeStateFilter === 'Archived',
      estimatedSavingsToDate: initiativeStateFilter === 'Archived',
      estimatedCo2eSavingsToDate: initiativeStateFilter === 'Archived',
      estimatedSavingsYearly: initiativeStateFilter === 'Archived',
      actualEnergyUsage: initiativeStateFilter !== 'Archived',
      energySavings: initiativeStateFilter === 'Archived',
      id: false,
    }),
    [initiativeStateFilter],
  );

  const columns = [
    {
      field: 'id',
      headerName: t('columns.id.header'),
      hidden: true,
    },
    {
      field: 'name',
      headerName: t('columns.name.header'),
      description: t('columns.name.description'),
      flex: 2,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Tooltip
              title={
                params.row.advancedSavingsAvailable
                  ? t('tooltips.advanced_model_used')
                  : t('tooltips.simple_model_used')
              }
            >
              <Avatar
                variant="rounded"
                sx={{
                  bgcolor: params.row.advancedSavingsAvailable
                    ? alpha('#c2dd00', 0.1)
                    : 'transparent',
                  mr: 1,
                  borderRadius: 3,
                }}
              >
                <ModelTrainingRoundedIcon
                  sx={(theme) => ({
                    color: params.row.advancedSavingsAvailable
                      ? '#c2dd00'
                      : theme.palette.mode === 'light'
                        ? 'lightgray'
                        : 'gray',
                  })}
                />
              </Avatar>
            </Tooltip>
            <Typography>{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'baselinePeriod',
      headerName: t('columns.duration.header'),
      description: t('columns.duration.description'),
      flex: 3,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          justifyContent={'center'}
        >
          <Box display="flex" gap={1}>
            <Typography variant="caption" color="textSecondary">
              {t('common.baseline_period')}:{' '}
            </Typography>
            <Typography variant="caption">
              {formatDateRange(
                params.row.baselineStart,
                params.row.baselineEnd,
              )}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Typography variant="caption" color="textSecondary">
              {t('common.comparison_period')}:{' '}
            </Typography>
            <Typography variant="caption">
              {formatDateRange(
                params.row.comparisonStart,
                params.row.comparisonEnd,
              )}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'estimatedSavingsToDate',
      headerName: t('columns.estimated_savings_to_date.header'),
      description: t('columns.estimated_savings_to_date.description'),
      flex: 2,
      minWidth: 180,
      renderCell: (params) => {
        const value = params?.value;
        const formattedValue = value ? formatNumber(value) : '-';
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            {value ? (
              <>
                <Typography minWidth={130}>
                  {t('datagrid.formats.kwh', { value: formattedValue })}
                </Typography>
                <Chip
                  variant="filled"
                  sx={{
                    marginLeft: 1,
                    borderRadius: 2,
                    backgroundColor: (theme) =>
                      alpha(
                        theme.palette[
                          getTrendColor(params.row.energySavingsPercentage)
                        ].main,
                        0.1,
                      ),
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette[
                            getTrendColor(params.row.energySavingsPercentage)
                          ].dark
                        : theme.palette[
                            getTrendColor(params.row.energySavingsPercentage)
                          ].light,
                  }}
                  label={
                    params.row.energySavingsPercentage
                      ? `${params.row.energySavingsPercentage.toFixed(1)}%`
                      : '0%'
                  }
                  size="small"
                />{' '}
              </>
            ) : (
              '-'
            )}
          </Box>
        );
      },
    },
    {
      field: 'estimatedCo2eSavingsToDate',
      headerName: t('columns.estimated_co2_savings_to_date.header'),
      description: t('columns.estimated_co2_savings_to_date.description'),
      flex: 2,
      minWidth: 180,
      renderCell: (params) => {
        const value = params?.value / 1000;
        const formattedValue = value ? formatNumber(value) : '-';
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            {value ? (
              <>
                <Typography minWidth={130}>
                  {t('datagrid.formats.co2e', { value: formattedValue })}
                </Typography>
                <Chip
                  variant="filled"
                  sx={{
                    marginLeft: 1,
                    borderRadius: 2,
                    backgroundColor: (theme) =>
                      alpha(
                        theme.palette[
                          getTrendColor(params.row.co2eSavingsPercentage)
                        ].main,
                        0.1,
                      ),
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette[
                            getTrendColor(params.row.co2eSavingsPercentage)
                          ].dark
                        : theme.palette[
                            getTrendColor(params.row.co2eSavingsPercentage)
                          ].light,
                  }}
                  label={
                    params.row.co2eSavingsPercentage
                      ? `${params.row.co2eSavingsPercentage.toFixed(1)}%`
                      : '0%'
                  }
                  size="small"
                />{' '}
              </>
            ) : (
              '-'
            )}
          </Box>
        );
      },
    },
    {
      field: 'actualEnergyUsage',
      headerName: t('columns.actual_energy_usage.header'),
      description: t('columns.actual_energy_usage.description'),
      flex: 2,
      renderCell: (params) => {
        const value = params?.value;
        const formattedValue = value ? formatNumber(value) : '-';
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            {value ? (
              <>
                <Typography minWidth={130}>
                  {t('datagrid.formats.kwh', { value: formattedValue })}
                </Typography>
              </>
            ) : (
              '-'
            )}
          </Box>
        );
      },
    },
    {
      field: 'state',
      headerName: t('columns.state.header'),
      description: t('columns.state.description'),
      flex: 1.5,
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            {/* //TODO Fix the name of states to match the cards */}
            <StateChip state={row.state} />
          </Box>
        );
      },
    },
    {
      field: 'timestampSummary',
      headerName: '',
      description: 'Summary of timestamps available for the initiative',
      renderCell: (params) => {
        const summary = params?.value;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <InitiativeTimestampSummaryIndicator
              timestampSummary={summary}
              initiative={params.row}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      description: t('columns.actions.description'),
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <InitiativeActionCell
            row={params.row}
            energyGroup={energyGroup}
            onView={(row) => {
              handleViewInitiative(row);
            }}
            onEdit={(row) => handleEditInitiative(row)}
            onDelete={(row) => handleDeleteInitiative(row)}
            onResetDates={(row) => resetDates(row.id)}
          />
        </Box>
      ),
    },
  ];

  const baseRows = initiatives.map((initiative) => ({
    id: Number(initiative.energyInitiativeId),
    name: initiative.name,
    description: initiative.description,
    baselineStart: initiative.baselineStart,
    baselineEnd: initiative.baselineEnd,
    comparisonStart: initiative.comparisonStart,
    comparisonEnd: initiative.comparisonEnd,
    energyTreeNodes: initiative.energyTreeNodes,
    state: initiative.state,
    createdDate: initiative.created,
    changedDate: initiative.changedDate,
    completedDate: initiative.completedDate,
    data: initiative.energySavings,
    timestampSummary: initiative.energySavings?.TimestampsSummary,
    actualCO2eEmission:
      initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
        ?.ActualCO2eEmission,
    actualEnergyUsage: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
          ?.ActualEnergyUsage
      : initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
          ?.ActualEnergyUsage,
    co2eSavings: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavings
      : initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavings,
    co2eSavingsPercentage: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavingsPercentage
      : initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavingsPercentage,
    averageBaselineEnergyUsage:
      initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.AverageBaselineEnergyUsage,
    averageComparisonEnergyUsage:
      initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.AverageComparisonEnergyUsage,
    energySavings: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
          ?.EnergySavings
      : initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
          ?.EnergySavings,
    energySavingsPercentage: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
          ?.EnergySavingsPercentage
      : initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
          ?.EnergySavingsPercentage,
    advancedSavingsAvailable: initiative.energySavings?.AdvancedModelUsed,
    advancedEnergySavings: initiative.energySavings?.AdvancedEnergySavings,
    estimatedSavingsToDate: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.ForecastedSavings?.AdvEstimatedTotalSavings
      : initiative.energySavings?.ForecastedSavings?.BasicEstimatedTotalSavings,
    estimatedSavingsYearly: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.ForecastedSavings?.AdvEstimatedYearlySavings
      : initiative.energySavings?.ForecastedSavings
          ?.BasicEstimatedYearlySavings,
    estimatedCo2eSavingsToDate: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.ForecastedSavings
          ?.AdvEstimatedTotalSavingsCO2e
      : initiative.energySavings?.ForecastedSavings
          ?.BasicEstimatedTotalSavingsCO2e,
    estimatedCo2eSavingsPercentageToDate: initiative.energySavings
      ?.AdvancedModelUsed
      ? initiative.energySavings?.ForecastedSavings?.AdvPercentageSavingsCO2e
      : initiative.energySavings?.ForecastedSavings
          ?.BasicEstimatedTotalSavingsCO2e,
    main: initiative,
  }));

  const rows = React.useMemo(
    () => baseRows.map((r) => ({ ...r, ...(overrides[r.id] || {}) })),
    [baseRows, overrides],
  );

  const filteredRows = useMemo(() => {
    if (!initiativeStateFilter) return rows;
    const filter = initiativeStateFilter.toLowerCase();
    if (filter === 'archived') {
      return rows.filter((r) => r.state === 'Archived');
    }
    if (filter === 'done') {
      return rows.filter((r) => ['Done', 'Active'].includes(r.state));
    }
    if (filter === 'created') {
      return rows.filter((r) => ['Created', 'Initialized'].includes(r.state));
    }
    return rows;
  }, [rows, initiativeStateFilter]);

  const getAccountId = () =>
    energyGroup?.AccountId ??
    energyGroup?.accountId ??
    energyGroup?.id ??
    energyGroup?.EnergyGroupId ??
    energyGroup?.energyGroupId;

  const persistDates = async (id, changes) => {
    const prevRow = baseRows.find((r) => r.id === id);
    if (!prevRow) {
      dispatch(
        showSnackbar({ severity: 'error', message: 'Initiativ ikke fundet.' }),
      );
      return;
    }
    const accountId = getAccountId();
    if (!accountId) {
      dispatch(
        showSnackbar({
          severity: 'error',
          message: 'Mangler accountId til opdatering.',
        }),
      );
      return;
    }

    setOverrides((o) => ({ ...o, [id]: { ...(o[id] || {}), ...changes } }));

    const baseMain = overrides[id]?.main || prevRow.main;
    const fullBody = buildPutBody(baseMain, changes);
    const nextState = deriveState(fullBody);

    const result = await putEnergyInitiative({
      accountId,
      energyInitiativeId: prevRow.id,
      updates: { ...fullBody, state: nextState },
    });

    if (!('error' in result)) {
      setOverrides((o) => ({
        ...o,
        [id]: {
          ...(o[id] || {}),
          ...changes,
          main: fullBody,
          state: nextState,
        },
      }));

      dispatch(showSnackbar({ severity: 'success', message: 'Datoer gemt.' }));
    } else {
      // Rollback optimistic state
      setOverrides((o) => {
        // eslint-disable-next-line no-unused-vars
        const { [id]: _, ...rest } = o;
        return rest;
      });
    }
    // Error snackbar is handled by apiErrorMiddleware (snackbar + App Insights).
  };
  const resetDates = (id) =>
    persistDates(id, {
      baselineStart: null,
      baselineEnd: null,
      comparisonStart: null,
      comparisonEnd: null,
    });

  return (
    <DataGridPremium
      localeText={localeText}
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      disableColumnResize
      disableRowSelectionOnClick
      disableColumnSorting
      disableColumnReorder
      rows={filteredRows}
      columns={columns}
      columnHeaderHeight={36}
      hideFooter
      pagination
      density="comfortable"
      disableSelectionOnClick
      pageSizeOptions={[10, 20, 50]}
      sortModel={[{ field: 'id', sort: 'desc' }]}
      columnVisibilityModel={columnVisibilityModel}
      loading={isLoading}
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
      }}
    />
  );
};

export default InitiativeDataGrid;
